import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { AuthShell, GoogleButton, Field, inputClass } from "@/pages/auth/authShared";
import { Check } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const { data: classes } = useQuery({ queryKey: ["classes"], queryFn: async () => (await api.get("/classes")).data });
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "" });
  const [classIds, setClassIds] = useState([]);
  const [notSure, setNotSure] = useState(false);
  const [consent, setConsent] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleClass = (id) => { setNotSure(false); setClassIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register({ ...form, class_ids: notSure ? [] : classIds, marketing_consent: consent, sms_opt_in: smsOptIn });
      toast.success("Account created — welcome to the club!");
      navigate(from, { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Join the club" subtitle="It's free — and takes less than a minute"
      footer={<>Already a member? <Link to="/login" state={{ from }} className="font-semibold text-[#B04B26] hover:underline">Log in</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><input required value={form.first_name} onChange={set("first_name")} className={inputClass} data-testid="register-firstname" /></Field>
          <Field label="Last name"><input required value={form.last_name} onChange={set("last_name")} className={inputClass} data-testid="register-lastname" /></Field>
        </div>
        <Field label="Email"><input type="email" required value={form.email} onChange={set("email")} className={inputClass} data-testid="register-email" autoComplete="email" /></Field>
        <Field label="Password" error={form.password && form.password.length < 8 ? "At least 8 characters" : ""}><input type="password" required minLength={8} value={form.password} onChange={set("password")} className={inputClass} data-testid="register-password" autoComplete="new-password" /></Field>
        <Field label="Mobile phone (optional)"><input value={form.phone} onChange={set("phone")} className={inputClass} data-testid="register-phone" placeholder="e.g. 07700 900123" /></Field>
        <p className="-mt-2 text-xs text-[#6E5B51]">UK mobile preferred. Needed if you'd like to receive SMS alerts.</p>

        <div>
          <p className="mb-2 text-sm font-semibold text-[#2D1E18]">Which classes do you attend?</p>
          <div className="space-y-2">
            {(classes || []).map((c) => {
              const on = classIds.includes(c.id);
              return (
                <button type="button" key={c.id} onClick={() => toggleClass(c.id)} data-testid={`register-class-${c.slug}`}
                  className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left text-sm ${on ? "border-[#C85A32] bg-[#FBEBE4]" : "border-border bg-white"}`}>
                  <span className="font-semibold text-[#2D1E18]">{c.name} <span className="font-normal text-[#6E5B51]">· {c.day}</span></span>
                  {on && <Check size={16} className="text-[#B04B26]" />}
                </button>
              );
            })}
            <button type="button" onClick={() => { setNotSure(true); setClassIds([]); }} data-testid="register-not-sure"
              className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left text-sm ${notSure ? "border-[#4A6B5D] bg-[#E8F0EC]" : "border-border bg-white"}`}>
              <span className="font-semibold text-[#2D1E18]">I'm not sure yet / haven't attended</span>
              {notSure && <Check size={16} className="text-[#4A6B5D]" />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-[#6E5B51]">You can change these anytime.</p>
        </div>

        <label className="flex items-start gap-2 text-sm text-[#5C4A42]">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="register-consent" className="mt-1" />
          <span>Send me occasional club news and updates by email (optional).</span>
        </label>
        <label className="flex items-start gap-2 text-sm text-[#5C4A42]">
          <input type="checkbox" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.target.checked)} data-testid="register-sms-optin" className="mt-1" />
          <span>Send me important <strong>SMS safety alerts</strong> — emergency cancellations, severe weather closures and last-minute venue changes. Requires a mobile number; separate from club news; you can turn this off anytime.</span>
        </label>

        {error && <p className="rounded-lg bg-[#FEF2F2] px-3 py-2 text-sm font-semibold text-[#991B1B]" data-testid="register-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="register-submit" className="w-full rounded-xl bg-[#C85A32] px-4 py-3 font-bold text-white hover:bg-[#B04B26] disabled:opacity-60">{loading ? "Creating account…" : "Create my account"}</button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-[#6E5B51]"><span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" /></div>
      <GoogleButton label="Sign up with Google" next={from} />
      <p className="mt-4 text-center text-xs text-[#6E5B51]">By joining you agree to our <Link to="/privacy" className="underline">privacy notice</Link>.</p>
    </AuthShell>
  );
}
